import { ConditionalLink } from '@/components/Common';
import { useAppContext } from '@/context/AppContext';
import { lazy, Suspense, useEffect, useRef } from 'react';
import {
  UseFormRegister,
  UseFormSetValue,
  UseFormWatch,
} from 'react-hook-form';
import toast from 'react-hot-toast';
import { ArrowRight, CrossIcon, HomeCross, HomeExclamation } from 'ui/icons';
import { Button } from 'ui/mobile-ds';
import clsx from 'clsx';
import useBreakpoints from '@/hooks/useBreakpoints';
import FormInput from '@/components/Common/FormInput/FormInput';

const Modal = lazy(() =>
  import('ui/retail-ds/Modal').then((mod) => ({
    default: mod.Modal,
  })),
);

const ModalOverlay = lazy(() =>
  import('ui/retail-ds/Modal').then((mod) => ({
    default: mod.ModalOverlay,
  })),
);

const ModalPortal = lazy(() =>
  import('ui/retail-ds/Modal').then((mod) => ({
    default: mod.ModalPortal,
  })),
);

const ModalContent = lazy(() =>
  import('ui/retail-ds/Modal').then((mod) => ({
    default: mod.ModalContent,
  })),
);

interface IProps {
  open: boolean;
  onOpenChange: (open?: boolean) => void;
  isInCreateFlow?: boolean;
  register?: UseFormRegister<{
    hospitalName?: string | undefined;
    hospitalId?: string | null | undefined;
    manualHospitalName?: string | null | undefined;
  }>;
  onSubmit: (e?: React.BaseSyntheticEvent) => Promise<void>;
  handleBlackListedCTAClick: () => void;
  blacklistHospitalUrl: string;
  setValue: UseFormSetValue<{
    hospitalName?: string | undefined;
    hospitalId?: string | null | undefined;
    manualHospitalName?: string | null | undefined;
  }>;
  watch: UseFormWatch<{
    hospitalName?: string | undefined;
    hospitalId?: string | null | undefined;
    manualHospitalName?: string | null | undefined;
  }>;
}

const CantFindHospitalModal = ({
  open,
  onOpenChange,
  isInCreateFlow,
  register,
  onSubmit,
  handleBlackListedCTAClick,
  blacklistHospitalUrl,
  setValue,
  watch,
}: IProps) => {
  const { isWebView } = useAppContext();
  const { isMobile } = useBreakpoints();
  const inputRef = useRef<HTMLInputElement | null>(null); // Reference for the input element
  useEffect(() => {
    if (open) {
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
        }
      }, 100);
    }
  }, [open]);

  const inputProps = register?.('manualHospitalName', {
    required: true,
  });

  const content = (
    <>
      <div
        className={clsx({
          'flex flex-col overflow-scroll no-scrollbar h-full': true,
        })}
      >
        <div className="flex justify-center relative">
          <p className="text-lg md:text-2xl font-chromatica text-mobile-text-main font-bold text-center">
            Avoid blacklisted hospitals
          </p>
          <CrossIcon
            className="absolute right-0 top-[50%] -translate-y-[50%] cursor-pointer w-6 h-6"
            onClick={() => {
              onOpenChange(false);
            }}
          />
        </div>
        <div
          className={clsx({
            'flex py-5 px-4 flex-col justify-center items-baseline text-left w-full mt-8 shadow-[0px_4px_14px_0px_rgba(0,0,0,0.05)] border border-mobile-gray-100 rounded-2xl':
              true,
            'max-w-[416px] mx-auto text-center items-center': isMobile,
          })}
        >
          <div className="flex">
            <div className="mr-3">
              <HomeExclamation className="w-5 h-5 md:w-6 md:h-6" />
            </div>
            <div>
              <p className="font-body font-semibold text-base text-mobile-text-main leading-5 text-left">
                Hospital could be non-network
              </p>
              <p className="font-body font-normal text-sm text-mobile-text-hierarchy mt-2 leading-[18px] text-left">
                Expenses may be reimbursed as long as the treatment is covered
                in the policy
              </p>
            </div>
          </div>
          <div className="my-4 border border-dashed border-mobile-gray-100 w-full" />
          <div className="flex">
            <div className="mr-3">
              <HomeCross className="w-5 h-5 md:w-6 md:h-6" />
            </div>
            <div>
              <p
                className={clsx({
                  'font-semibold text-base text-mobile-text-main leading-5 text-left':
                    true,
                  'text-sm': isMobile,
                })}
              >
                Hospital could be blacklisted / excluded
              </p>
              <p className="font-body font-normal text-sm text-mobile-text-hierarchy mt-2 leading-[18px] text-left">
                Expenses at these hospitals may not be covered under cashless or
                reimbursement
                <ConditionalLink
                  external
                  href={isWebView ? null : blacklistHospitalUrl}
                  onClick={handleBlackListedCTAClick}
                  className="text-sm h-fit !text-mobile-hero-100 flex items-center text-left py-2"
                >
                  Learn more and view the list here
                  <ArrowRight className="w-4 h-4 ml-1" />
                </ConditionalLink>
              </p>
            </div>
          </div>
        </div>
        {isInCreateFlow && (
          <>
            <div className="my-6 md:my-8 border border-dashed border-mobile-gray-100 w-full" />
            <div className="text-left flex-1 flex flex-col">
              <p className="text-sm md:text-base font-body text-mobile-text-main font-semibold">
                You can enter your hospital name manually
              </p>
              <FormInput
                label="Hospital name"
                className="my-2"
                inputProps={{
                  id: 'manualHospitalName',
                  placeholder: 'Enter your hospital manually to continue',
                  ...inputProps,
                  ref: (node) => {
                    inputProps?.ref(node);
                    inputRef.current = node;
                  },
                  onFocus: (e) => {
                    if (isMobile) {
                      e.preventDefault();
                      setTimeout(() => {
                        inputRef?.current?.scrollIntoView({
                          behavior: 'smooth',
                        });
                      }, 200);
                    }
                  },
                }}
                helper={{
                  text: 'Always ensure your hospital is not a part of your insurers blacklist/exclusion list',
                }}
              />
              <div
                className={clsx({
                  'pt-4': true,
                  'flex flex-row-reverse mt-8 items-center gap-8': !isMobile,
                  'flex flex-col mt-auto bg-white sticky bottom-0': isMobile, // Added sticky class for mobile
                })}
              >
                <Button
                  type="submit"
                  className={clsx({ 'w-full': isMobile })}
                  onClick={(event) => {
                    // get the manual hospital name value (text input field)
                    // and then set it to the hospital name, so that it allows the form to submit
                    // and also set the hospital id to null
                    const manualHospitalName = watch('manualHospitalName');
                    if (!manualHospitalName) {
                      event.preventDefault();
                      toast('Please enter the hospital name');
                    } else {
                      setValue('hospitalName', manualHospitalName);
                      setValue('hospitalId', null);
                      onSubmit();
                    }
                  }}
                >
                  Continue
                </Button>
                <Button
                  type="submit"
                  onClick={() => {
                    // set both manual and hospitalName to a 'DUMMY' string
                    // and also set hospital id to null
                    // so that no hospital is selected
                    // we'll check for the DUMMY string and remove it while API call
                    setValue('manualHospitalName', '');
                    setValue('hospitalName', '');
                    setValue('hospitalId', null);
                    onSubmit();
                  }}
                  intent={'tertiary'}
                >
                  I haven't finalised the hospital yet
                </Button>
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
  return (
    <Suspense>
      <Modal onOpenChange={onOpenChange} open={open}>
        <ModalPortal>
          <ModalOverlay />
          <ModalContent
            className={clsx({
              'flex w-[566px] flex-col p-8 outline-none rounded-2xl': !isMobile,
              'flex w-full flex-col rounded-none h-[100dvh] p-6 pb-2': isMobile,
            })}
          >
            <div className="text-center flex flex-col h-full">{content}</div>
          </ModalContent>
        </ModalPortal>
      </Modal>
    </Suspense>
  );
};

export default CantFindHospitalModal;
